import { ApplyResultSummary, ApplyReasonCode, ApplyDiagnostic } from "../types";
import { classifyApplyResult, countSuccessfulOps } from "../services/slideService";

function mergeReasonCounts(
  left: Partial<Record<ApplyReasonCode, number>>,
  right: Partial<Record<ApplyReasonCode, number>>
): Partial<Record<ApplyReasonCode, number>> {
  const merged: Partial<Record<ApplyReasonCode, number>> = { ...left };
  for (const [code, value] of Object.entries(right)) {
    const typedCode = code as ApplyReasonCode;
    merged[typedCode] = (merged[typedCode] || 0) + (value || 0);
  }
  return merged;
}

export function mergeApplySummaries(
  primary: ApplyResultSummary,
  secondary: ApplyResultSummary | null
): ApplyResultSummary {
  if (!secondary) {
    return primary;
  }

  const merged: ApplyResultSummary = {
    requested: {
      slides: primary.requested.slides + secondary.requested.slides,
      shapes: primary.requested.shapes + secondary.requested.shapes,
      byAction: {
        create: primary.requested.byAction.create + secondary.requested.byAction.create,
        modify: primary.requested.byAction.modify + secondary.requested.byAction.modify,
        delete: primary.requested.byAction.delete + secondary.requested.byAction.delete,
        duplicate: primary.requested.byAction.duplicate + secondary.requested.byAction.duplicate,
      },
    },
    applied: {
      slides: primary.applied.slides + secondary.applied.slides,
      shapes: primary.applied.shapes + secondary.applied.shapes,
      byAction: {
        create: primary.applied.byAction.create + secondary.applied.byAction.create,
        modify: primary.applied.byAction.modify + secondary.applied.byAction.modify,
        delete: primary.applied.byAction.delete + secondary.applied.byAction.delete,
        duplicate: primary.applied.byAction.duplicate + secondary.applied.byAction.duplicate,
      },
    },
    skipped: {
      count: primary.skipped.count + secondary.skipped.count,
      byReason: mergeReasonCounts(primary.skipped.byReason, secondary.skipped.byReason),
      issues: [...primary.skipped.issues, ...secondary.skipped.issues],
    },
    errors: {
      count: primary.errors.count + secondary.errors.count,
      byReason: mergeReasonCounts(primary.errors.byReason, secondary.errors.byReason),
      issues: [...primary.errors.issues, ...secondary.errors.issues],
    },
    classification: "true_no_op",
  };

  merged.classification = classifyApplyResult(merged);
  return merged;
}

export function findPrimaryIssue(summary: ApplyResultSummary): ApplyDiagnostic | undefined {
  const byCategory = (category: ApplyDiagnostic["category"]) =>
    summary.skipped.issues.find((issue) => issue.category === category);

  const preferredModelOutputCodes: ApplyReasonCode[] = [
    "MODEL_OUTPUT_CHART_RISK_REJECTED",
    "MODEL_OUTPUT_CHART_RISK_REMAPPED",
    "MODEL_OUTPUT_INVALID_NATIVE_CHART",
  ];
  const preferredModelIssue = summary.skipped.issues.find((issue) =>
    preferredModelOutputCodes.includes(issue.code)
  );

  if (summary.classification === "target_mismatch") {
    return byCategory("target_mismatch") || summary.skipped.issues[0] || summary.errors.issues[0];
  }
  if (summary.classification === "model_output_mismatch") {
    return (
      preferredModelIssue ||
      byCategory("model_output_mismatch") ||
      summary.skipped.issues[0] ||
      summary.errors.issues[0]
    );
  }
  if (summary.classification === "apply_failed") {
    return summary.errors.issues[0] || byCategory("apply_error") || summary.skipped.issues[0];
  }
  if (summary.classification === "true_no_op") {
    return byCategory("no_op") || summary.skipped.issues[0];
  }
  return summary.errors.issues[0] || summary.skipped.issues[0];
}

const FRIENDLY_CLASSIFICATION_MESSAGES: Record<ApplyResultSummary["classification"], string> = {
  applied: "Applied your request.",
  partial_apply: "Applied part of your request.",
  true_no_op: "No changes made.",
  target_mismatch: "No changes made because I couldn't find the target on the slide.",
  model_output_mismatch: "No changes made because I couldn't generate valid edit instructions.",
  apply_failed: "No changes made because PowerPoint couldn't apply the edit.",
};

const FRIENDLY_REASON_HINTS: Partial<Record<ApplyReasonCode, string>> = {
  NO_MODIFICATIONS_RETURNED:
    'Try naming the exact object and slide (for example: "On slide 10, delete the brown rectangle behind the title.")',
  SHAPE_ID_NOT_FOUND: "Try describing the object by visible text, color, and location.",
  SLIDE_INDEX_OUT_OF_RANGE: "Go to the target slide and run the request again.",
  NO_MUTATION_FIELDS:
    "Specify exactly what should change (text, color, size, position, or delete).",
  MODEL_OUTPUT_MISSING_SLIDES: "Please retry with one clear action.",
  MODEL_OUTPUT_PARSE_FALLBACK_EMPTY: "Please retry with one clear action.",
  MODEL_OUTPUT_INVALID_NATIVE_CHART:
    "Please include chart type, categories, and at least one series with values.",
  MODEL_OUTPUT_CHART_OPTION_DROPPED:
    "Some requested chart options are unsupported; try a simpler chart request.",
  MODEL_OUTPUT_CHART_OPTION_NORMALIZED:
    "Some chart options were normalized automatically to a valid format.",
  MODEL_OUTPUT_CHART_TYPE_REMAPPED:
    "Requested chart type was remapped to a supported type.",
  MODEL_OUTPUT_CHART_RISK_REMAPPED:
    "A risky combo chart request was remapped to a safer editable combo.",
  MODEL_OUTPUT_CHART_RISK_REJECTED:
    "That chart combination is known to produce malformed files; try bar + line instead.",
  MODEL_OUTPUT_CHART_LAYOUT_CLAMPED:
    "Chart layout was clamped to fit the slide safely.",
  MODEL_OUTPUT_CHART_SERIES_NORMALIZED:
    "Series values were normalized to match chart categories.",
  MODEL_OUTPUT_CHART_PERCENT_COERCED:
    "Percent-like series values were normalized to fractional scale (for example 18 to 0.18).",
  SHAPE_CREATE_FAILED: "Try a simpler one-step create request.",
  SHAPE_MODIFY_FAILED: "Try modifying one object at a time.",
  SHAPE_DELETE_FAILED: "Try selecting the object and retrying.",
  TABLE_INSERT_FAILED: "Try creating the table in a separate request.",
  CHART_INSERT_FAILED: "Try creating the chart in a separate request.",
  IMAGE_PREFETCH_FAILED: "Try a direct public image URL or a different image source.",
  IMAGE_INSERT_UNSUPPORTED: "Try a PNG/JPG image URL.",
  SLIDE_DUPLICATE_FAILED: "Try duplicating manually first, then request edits.",
};

function isUndoLikeQuery(query: string): boolean {
  return /\b(undo|revert|restore|add (it|that) back|bring (it|that) back)\b/i.test(query);
}

export function buildApplyStatusMessage(
  summary: ApplyResultSummary,
  query: string = ""
): { success: boolean; message: string } {
  const appliedCount = countSuccessfulOps(summary);
  if (appliedCount > 0) {
    const suffixParts: string[] = [];
    if (summary.skipped.count > 0) suffixParts.push(`${summary.skipped.count} skipped`);
    if (summary.errors.count > 0)
      suffixParts.push(`${summary.errors.count} error${summary.errors.count === 1 ? "" : "s"}`);
    const suffix = suffixParts.length > 0 ? ` (${suffixParts.join(", ")})` : "";
    return {
      success: true,
      message: `Applied ${appliedCount} change${appliedCount === 1 ? "" : "s"}${suffix}`,
    };
  }

  const primaryIssue = findPrimaryIssue(summary);
  const reasonCode = primaryIssue?.code;
  const baseMessage =
    FRIENDLY_CLASSIFICATION_MESSAGES[summary.classification] || "No changes made.";
  const reasonHint = (reasonCode && FRIENDLY_REASON_HINTS[reasonCode]) || "";

  if (
    summary.classification === "true_no_op" &&
    reasonCode === "NO_MODIFICATIONS_RETURNED" &&
    isUndoLikeQuery(query)
  ) {
    return {
      success: false,
      message:
        "No changes made. I couldn't infer what to restore. Use PowerPoint Undo (Cmd+Z / Ctrl+Z), or tell me exactly what to bring back (object + slide).",
    };
  }

  return {
    success: false,
    message: reasonHint ? `${baseMessage} ${reasonHint}` : baseMessage,
  };
}
